import { Controller } from "@hotwired/stimulus"
import { formatShortcut, shortcuts } from "../keyboard/commands"

export default class extends Controller {
  static targets = ["dialog", "input", "results", "status", "shortcut", "clearButton", "allResults"]

  connect() {
    const trigger = this.element.querySelector('[data-action="global-search#open"]')
    trigger.dataset.shortcutHintManaged = "external"
    this.unregisterShortcut = shortcuts.register({
      id: "search.open", label: trigger.getAttribute("aria-label"),
      element: trigger, action: () => this.open()
    })
    this.refreshHint()
  }

  refreshHint() {
    const trigger = this.element.querySelector('[data-action="global-search#open"]')
    const keys = shortcuts.bindingFor("search.open")
    this.shortcutTarget.textContent = formatShortcut(keys)
    this.shortcutTarget.classList.toggle("d-none", !keys)
    this.shortcutTarget.classList.toggle("xl:d-inline", Boolean(keys))
    trigger.title = [trigger.getAttribute("aria-label"), formatShortcut(keys)].filter(Boolean).join(" · ")
    if (keys) trigger.setAttribute("aria-keyshortcuts", keys.replace("mod", shortcuts.mac ? "Meta" : "Control"))
    else trigger.removeAttribute("aria-keyshortcuts")
  }

  open(event) {
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return
    event?.preventDefault()
    if (this.dialogTarget.open) return

    this.returnFocus = document.activeElement
    this.dialogTarget.showModal()
    this.inputTarget.focus()
    this.inputTarget.select()
    this.search()
  }

  search() {
    this.stopRequest()
    const query = this.inputTarget.value.trim()
    this.clearButtonTarget.hidden = this.inputTarget.value.length === 0
    this.allResultsTarget.hidden = query.length < 2
    const url = new URL(this.inputTarget.form.action)
    url.searchParams.set("query", query)
    this.allResultsTarget.href = `${url.pathname}${url.search}`
    // Remove previous links immediately so Enter cannot open a stale result.
    this.resultsTarget.replaceChildren()
    if (query.length < 2) {
      this.setState("idle", "prompt")
      return
    }

    this.setState("loading", "loading")
    this.resultsTarget.setAttribute("aria-busy", "true")
    this.timer = window.setTimeout(() => this.load(query), 250)
  }

  async load(query) {
    const request = new AbortController()
    this.request = request
    const url = new URL(this.inputTarget.form.action)
    url.searchParams.set("query", query)
    url.searchParams.set("preview", "1")
    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: "text/html" }, cache: "no-store" })
      if (!response.ok || response.redirected) throw new Error("Search failed")
      const html = await response.text()
      if (this.request !== request) return

      this.resultsTarget.innerHTML = html
      this.setState("ready", "ready")
    } catch (error) {
      if (error.name !== "AbortError" && this.request === request) this.setState("error", "failure")
    } finally {
      if (this.request === request) {
        this.request = null
        this.resultsTarget.setAttribute("aria-busy", "false")
      }
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.search()
    this.inputTarget.focus()
  }

  setState(state, message) {
    this.dialogTarget.dataset.state = state
    this.statusTarget.textContent = this.statusTarget.dataset[message]
  }

  submit(event) {
    const active = this.resultsTarget.querySelector("[data-global-search-result]:focus")
    if (active) {
      event.preventDefault()
      active.click()
    }
  }

  navigate(event) {
    if (event.defaultPrevented || event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return
    const links = [...this.resultsTarget.querySelectorAll("[data-global-search-result]")]
    if (!links.length) return
    if (event.key === "Enter") {
      if (event.target !== this.inputTarget) return
      event.preventDefault()
      links[0].click()
      return
    }
    if (event.target !== this.inputTarget && !links.includes(event.target)) return
    event.preventDefault()
    const current = links.indexOf(document.activeElement)
    const next = current === -1 ? (event.key === "ArrowDown" ? 0 : links.length - 1) : (current + (event.key === "ArrowDown" ? 1 : -1) + links.length) % links.length
    links[next].focus()
  }

  follow(event) {
    if (event.target.closest("a")) this.close()
  }

  backdrop(event) {
    if (event.target !== this.dialogTarget) return
    const rect = this.dialogTarget.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close()
  }

  cancel(event) {
    event.preventDefault()
    this.close()
  }

  close() {
    this.stopRequest()
    this.dialogTarget.close()
  }

  closed() {
    this.returnFocus?.focus({ preventScroll: true })
  }

  stopRequest() {
    window.clearTimeout(this.timer)
    this.request?.abort()
    this.request = null
    this.resultsTarget.setAttribute("aria-busy", "false")
  }

  reset() {
    this.close()
    this.inputTarget.value = ""
    this.search()
  }

  disconnect() {
    this.unregisterShortcut?.()
    this.stopRequest()
  }
}
